import React, { useState } from 'react';
import SEO from '../components/common/SEO';
import BlogHeroSection from '../components/blog/BlogHeroSection';
import BlogFilterSection from '../components/blog/BlogFilterSection';
import BlogGridSection from '../components/blog/BlogGridSection';
import { blogCategories, blogPosts } from '../data/blogData';

const INITIAL_COUNT = 12;

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setVisibleCount(INITIAL_COUNT);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setVisibleCount(INITIAL_COUNT);
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    setVisibleCount(INITIAL_COUNT);
  };

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'ALL' ||
      post.category.toUpperCase() === selectedCategory.toUpperCase();

    const q = searchQuery.toLowerCase().trim();
    const contentText = Array.isArray(post.content)
      ? post.content.join(' ')
      : (post.content || '');

    const matchesSearch =
      q === '' ||
      (post.title && post.title.toLowerCase().includes(q)) ||
      (post.category && post.category.toLowerCase().includes(q)) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(q)) ||
      contentText.toLowerCase().includes(q);

    if (sortBy === 'recent') {
      const postDate = new Date(post.date);
      const today = new Date();
      const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      const sevenDaysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7, 0, 0, 0, 0);

      const matchesRecent = postDate >= sevenDaysAgo && postDate <= endOfToday;
      return matchesCategory && matchesSearch && matchesRecent;
    }

    return matchesCategory && matchesSearch;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'recent' || sortBy === 'newest') {
      return new Date(b.date) - new Date(a.date);
    }
    if (sortBy === 'oldest') {
      return new Date(a.date) - new Date(b.date);
    }
    if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  const displayedPosts = sortedPosts.slice(0, visibleCount);
  const hasMore = visibleCount < sortedPosts.length;

  const toggleVisible = () => {
    if (hasMore) {
      setVisibleCount(sortedPosts.length);
    } else {
      setVisibleCount(INITIAL_COUNT);
    }
  };

  return (
    <main className="w-full min-h-screen bg-[#f9f9f5] pt-[40px] md:pt-[60px] pb-24 text-[#1C1917]">
      <SEO
        title="Blog & Insights"
        description="Read our latest articles on web design, SEO strategies, brand building, wallpaper downloads, and creative business tips."
        keywords="web design blog, SEO tips, creative business advice, showit tutorials, wellness website tips"
      />
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8">
        <BlogHeroSection />
        <BlogFilterSection
          categories={blogCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
        <BlogGridSection
          displayedPosts={displayedPosts}
          filteredPostsLength={sortedPosts.length}
          initialCount={INITIAL_COUNT}
          hasMore={hasMore}
          onToggleVisible={toggleVisible}
          sortBy={sortBy}
          onSortChange={handleSortChange}
        />
      </div>
    </main>
  );
};

export default Blog;
